import React, { useEffect, useState, useCallback } from "react";
import {
  Collapse,
  Button,
  Empty,
  Spin,
  Switch,
  Input,
  Checkbox,
  Space,
  message,
  Radio,
  Modal,
  Select,
  Form,
} from "antd";
import {
  LoadingOutlined,
  SearchOutlined,
  FilterOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import useNotificationData from "../hooks/useNotificationData";

const NotificationRulesManager = () => {
  const { rules, loading, fetchRules, createRule, updateRule, toggleRule } =
    useNotificationData();
  const [editingRule, setEditingRule] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [searchText, setSearchText] = useState("");
  const [filterStatus, setFilterStatus] = useState("active");
  const [filterChannels, setFilterChannels] = useState([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const [form] = Form.useForm();

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  useEffect(() => {
    fetchRules();
  }, []);

  const filteredRules = useCallback(() => {
    return rules
      .filter((rule) => {
        const matchesSearch =
          (rule.eventType || "")
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          (rule.description || "")
            .toLowerCase()
            .includes(searchText.toLowerCase());

        const matchesStatus =
          filterStatus === "all" ||
          (filterStatus === "active" && rule.isActive) ||
          (filterStatus === "inactive" && !rule.isActive);

        const matchesChannel =
          filterChannels.length === 0 ||
          filterChannels.some((ch) => rule.channels.includes(ch));

        return matchesSearch && matchesStatus && matchesChannel;
      })
      .sort((a, b) => a.eventType.localeCompare(b.eventType));
  }, [rules, searchText, filterStatus, filterChannels]);

  const handleEdit = (rule) => {
    setEditingRule(rule.id);
    setFormData({
      channels: rule.channels,
      notificationType: "INSTANT",
      requiresOptIn: rule.requiresOptIn,
      isActive: rule.isActive,
      description: rule.description,
      bccEmail: rule.bccEmail || "",
    });
  };

  const handleCreate = () => {
    form.resetFields();
    setCreateModalOpen(true);
  };

  const handleSaveCreate = async (values) => {
    const payload = {
      ...values,
      channels: values.channels?.join(",") || "",
      notificationType: "INSTANT",
    };

    const result = await createRule(payload);
    if (result.success) {
      message.success("Rule created successfully");
      setCreateModalOpen(false);
      form.resetFields();
    } else {
      message.error("Failed to create rule");
    }
  };

  const handleSave = async (eventType) => {
    // if (!formData.channels.trim()) {
    //   message.error("At least one channel is required");
    //   return;
    // }
    const result = await updateRule(eventType, formData);
    if (result.success) {
      message.success("Rule updated successfully");
      setEditingRule(null);
    } else {
      message.error("Failed to update rule");
    }
  };

  const handleToggle = async (eventType, currentStatus) => {
    const result = await toggleRule(eventType, !currentStatus);
    if (result.success) {
      message.success(`Rule ${!currentStatus ? "enabled" : "disabled"}`);
    } else {
      message.error("Failed to toggle rule");
    }
  };

  const handleChannelChange = (ch, checked) => {
    let channels = formData.channels.split(",").filter((c) => c.trim());
    if (checked) {
      if (!channels.includes(ch)) channels.push(ch);
    } else {
      channels = channels.filter((c) => c !== ch);
    }
    setFormData({ ...formData, channels: channels.join(",") });
  };

  const handleFilterChannelChange = (ch) => {
    let newChannels = [...filterChannels];
    if (newChannels.includes(ch)) {
      newChannels = newChannels.filter((c) => c !== ch);
    } else {
      newChannels.push(ch);
    }
    setFilterChannels(newChannels);
  };

  const renderCollapseItems = () => {
    return filteredRules().map((rule) => ({
      key: rule.id,
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <p style={{ display: "inline", margin: 0 }}>
            <strong>{rule.eventType}</strong>
          </p>
          <span style={{ fontSize: "12px", color: "#999" }}>
            {rule.channels} • {rule.recipientType}
          </span>
        </div>
      ),
      children: (
        <div>
          {editingRule === rule.id ? (
            <div className="space-y-4">
              <div>
                <label className="block  font-medium mb-2">Description</label>
                <Input
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="What is this notification about?"
                />
              </div>



              <div>
                <label className="block text-sm font-medium mb-2">
                  BCC Email
                </label>
                <Input
                  placeholder="admin@company.com (optional)"
                  type="email"
                  value={formData.bccEmail || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      bccEmail: e.target.value,
                    })
                  }
                />
                <p className="text-xs text-gray-500 mt-1">
                  Email address to receive a copy of all notifications for this
                  rule
                </p>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      isActive: e.target.checked,
                    })
                  }
                />
                <span className="">Active</span>
              </label>

              <Space className="w-full justify-between">
                <Button onClick={() => setEditingRule(null)}>Cancel</Button>
                <Button
                  type="primary"
                  onClick={() => handleSave(rule.eventType)}
                >
                  Save
                </Button>
              </Space>
            </div>
          ) : (
            <ul className="mobile-list-flex">
              <li>
                <span>Channels:</span>
                <span className="font-medium">{rule.channels}</span>
              </li>
              <li>
                <span>Recipient:</span>
                <span className="capitalize">{rule.recipientType}</span>
              </li>
              {rule.bccEmail && (
                <li>
                  <span>BCC Email:</span>
                  <span>{rule.bccEmail}</span>
                </li>
              )}
              <li>
                <span>Opt-In Required:</span>
                <span>{rule.requiresOptIn ? "✓ Yes" : "✗ No"}</span>
              </li>
              <li>
                <span>Status:</span>
                <span
                  className={
                    rule.isActive
                      ? "text-green-600 font-medium"
                      : "text-red-600 font-medium"
                  }
                >
                  {rule.isActive ? "Active" : "Inactive"}
                </span>
              </li>
              <li>
                <span>Description:</span>
                <span className="text-gray-600">{rule.description}</span>
              </li>
              <li>
                <span>Action:</span>
                <Button
                  size="small"
                  type="primary"
                  onClick={() => handleEdit(rule)}
                >
                  Edit
                </Button>
              </li>
            </ul>
          )}
        </div>
      ),
    }));
  };

  return (
    <Spin spinning={loading} indicator={antIcon}>
      <div className="pb-10">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold mb-0">Notification Rules</h3>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Create New Rule
            </Button>
          </div>

          <div className="bg-white p-4 rounded-lg mb-4 flex gap-2">
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search rules..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ flex: 1 }}
            />
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFilterModalOpen(true)}
            />
          </div>
        </div>

        {filteredRules().length === 0 ? (
          <Empty
            description={
              searchText || filterStatus !== "all" || filterChannels.length > 0
                ? "No rules match filters"
                : "No rules found"
            }
          />
        ) : (
          <div className="mobile topup-data">
            <Collapse items={renderCollapseItems()} defaultActiveKey={[0]} />
          </div>
        )}
      </div>

      {/* Creation Modal */}
      <Modal
        title="Create New Notification Rule"
        open={createModalOpen}
        onOk={() => form.submit()}
        onCancel={() => setCreateModalOpen(false)}
        width="90%"
        style={{ maxWidth: "600px" }}
        styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
        centered
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveCreate}
          initialValues={{
            notificationType: "INSTANT",
            recipientType: "buyer",
            isActive: true,
          }}
        >
          <Form.Item
            name="eventType"
            label="Event Type"
            rules={[
              { required: true, message: "Required" },
              {
                pattern: /^[A-Z0-9_]+$/,
                message: "Use uppercase, numbers and underscores only",
              },
            ]}
            tooltip="User internal identifier like 'WELCOME_EMAIL' or 'MONDAY_PROMO'"
          >
            <Input placeholder="e.g. MONDAY_PROMO" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="What is this notification about?" />
          </Form.Item>

          <Form.Item
            name="recipientType"
            label="Recipient Type"
            rules={[{ required: true, message: "Required" }]}
          >
            <Select>
              <Select.Option value="buyer">Buyer</Select.Option>
              <Select.Option value="driver">Driver</Select.Option>
              <Select.Option value="restaurant">
                Business/Restaurant
              </Select.Option>
            </Select>
          </Form.Item>



          <Form.Item name="bccEmail" label="BCC Email (Optional)">
            <Input placeholder="admin@company.com" type="email" />
          </Form.Item>

          <Form.Item name="isActive" valuePropName="checked">
            <Checkbox>Active</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      {/* Filter Modal */}
      <Modal
        title="Filter Rules"
        open={filterModalOpen}
        onCancel={() => setFilterModalOpen(false)}
        footer={[
          <Button
            key="reset"
            onClick={() => {
              setFilterStatus("all");
              setFilterChannels([]);
            }}
          >
            Reset
          </Button>,
          <Button
            key="close"
            type="primary"
            onClick={() => setFilterModalOpen(false)}
          >
            Done
          </Button>,
        ]}
        centered
      >
        <div className="space-y-6">
          <div>
            <label className="block font-medium mb-3">Status</label>
            <Radio.Group
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <div className="space-y-2">
                <Radio value="all">All</Radio>
                <Radio value="active">Active</Radio>
                <Radio value="inactive">Inactive</Radio>
              </div>
            </Radio.Group>
          </div>

          <div>
            <label className="block font-medium mb-3">Channels</label>
            <div className="space-y-2">
              {["PUSH", "SMS", "EMAIL", "WHATSAPP"].map((ch) => (
                <label
                  key={ch}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Checkbox
                    checked={filterChannels.includes(ch)}
                    onChange={() => handleFilterChannelChange(ch)}
                  />
                  <span>{ch}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </Spin>
  );
};

export default NotificationRulesManager;
